import { defineStore } from "pinia";

import requisitesApi from "@/services/api/requisitesApi";
import requisiteTypesApi from "@/services/api/requisiteTypesApi";
import attachmentsApi from "@/services/api/attachmentsApi";

import { getCompanyId } from "@/utils/userUtils";
import { isImage, resolveUrl } from "@/utils/fileUtils";
import { formatDate } from "@/utils/dateUtils";

export const useRequisiteStore = defineStore("requisite", {
  state: () => ({
    companyId: getCompanyId(),

    requisites: [],
    requisiteTypes: [],
    attachmentIds: {},
    existingAttachments: {},
    loading: false,

    customFields: [],
    customFieldsLoading: false,
    customFieldsPagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },

    availableModels: [
      { value: "CompanyUser", label: "Пользователь" },
      { value: "Vehicle", label: "Транспорт" },
      { value: "Company", label: "Компания" },
      { value: "Counterparty", label: "Контрагент" },
      { value: "Order", label: "Заказ" },
      { value: "Trip", label: "Рейс" },
      { value: "Driver", label: "Водитель" },
    ],

    fieldTypeNames: {
      text: "Текст",
      string: "Строка",
      number: "Число",
      date: "Дата",
      select: "Выбор из списка",
    },

    fieldTypeOptions: [
      { value: "string", label: "Строка" },
      { value: "text", label: "Текст" },
      { value: "number", label: "Число" },
      { value: "date", label: "Дата" },
      { value: "select", label: "Выбор из списка" },
    ],
  }),

  getters: {
    hasRequisiteData: (state) => (requisiteType) => {
      const req = state.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );
      return req && Object.values(req.data).some((v) => v);
    },

    getFieldValue: (state) => (requisiteType, fieldCode) => {
      const req = state.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );
      const value = req?.data[fieldCode];
      if (!value) return "—";

      // Находим поле в схеме
      const field = requisiteType.schema?.fields?.find(
        (f) => f.code === fieldCode
      );

      if (!field) return value;

      // Обработка select поля
      if (field.type === "select" && field.options) {
        const selectedOption = field.options.find(
          (opt) => opt.value === value
        );
        return selectedOption?.label || value;
      }

      // Обработка date поля
      if (field.type === "date") {
        return formatDate(value);
      }

      return value;
    },

    getRequisiteCreateTime: (state) => (requisiteType) => {
      const req = state.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );
      return req?.created_at;
    },

    getRequisiteUpdateTime: (state) => (requisiteType) => {
      const req = state.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );
      return req?.updated_at;
    },

    getAttachments: (state) => (requisiteTypeId) => {
      return state.existingAttachments[requisiteTypeId] || [];
    },
  },

  actions: {
    async getCustomFieldsRequest(params = {}) {
      this.customFieldsLoading = true;
      try {
        const response = await requisiteTypesApi.index(
          this.companyId,
          params
        );

        this.customFields = response.data.result || [];

        const meta = response.data.pagination_meta;
        this.customFieldsPagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
        };
      } finally {
        this.customFieldsLoading = false;
      }
    },

    async createCustomField(payload) {
      const response = await requisiteTypesApi.create(
        this.companyId,
        payload
      );
      return response.data;
    },

    async updateCustomField(id, payload) {
      await requisiteTypesApi.update(this.companyId, id, payload);
    },

    async toggleCustomField(id) {
      await requisiteTypesApi.toggle(this.companyId, id);
    },

    updateLocalCustomField(updatedField) {
      const target = this.customFields.find(
        (f) => f.id === updatedField.id
      );
      if (target) {
        Object.keys(target).forEach((key) => delete target[key]);
        Object.assign(target, updatedField);
      }
    },

    async loadRequisites({ id, allowed_models, requisitable_type }) {
      this.loading = true;
      try {
        const [typesRes, requisitesRes] = await Promise.all([
          requisiteTypesApi.index(this.companyId, {
            allowed_models,
            enabled: true,
          }),
          requisitesApi.index(this.companyId, {
            requisitable_id: id,
            requisitable_type,
          }),
        ]);

        this.requisiteTypes = typesRes.data.result || [];
        this.requisites = requisitesRes.data.result || [];

        await this.prepareRequisites();
        await this.loadAttachments();
      } catch (error) {
        console.error("Ошибка при загрузке пользовательских полей:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Подготовка структуры для пользовательских полей
    async prepareRequisites() {
      // Инициализация хранилищ
      this.requisiteTypes.forEach((type) => {
        this.attachmentIds[type.id] = [];
        this.existingAttachments[type.id] = [];
      });

      // Сбор attachment ID
      const attachmentPromises = [];
      this.requisites.forEach((req) => {
        const typeId = req.requisite_type.id;
        const typeExists = this.requisiteTypes.some(
          (t) => t.id === typeId
        );

        if (typeExists) {
          req.attachments?.forEach((att) => {
            attachmentPromises.push(att.id);
          });
        }
      });

      return attachmentPromises;
    },

    // Загрузка вложений
    async loadAttachments() {
      const attachmentPromises = [];

      this.requisites.forEach((req) => {
        const typeId = req.requisite_type.id;

        req.attachments?.forEach((att) => {
          attachmentPromises.push(
            attachmentsApi
              .show(this.companyId, att.id)
              .then((res) => ({
                attachmentId: att.id,
                requisiteTypeId: typeId,
                data: res.data.result,
              }))
              .catch((error) => {
                console.error("Ошибка при загрузке файла", error);
                return null;
              })
          );
        });
      });

      if (attachmentPromises.length > 0) {
        const results = await Promise.all(attachmentPromises);
        this.processAttachments(results);
      }
    },

    // Обработка загруженных вложений
    processAttachments(results) {
      results.forEach((result) => {
        if (!result) return;
        const { attachmentId, requisiteTypeId, data } = result;

        if (!this.existingAttachments[requisiteTypeId]) return;

        const file = data.file;
        const fileUrl = data.file_url;

        const fullAttachment = {
          id: attachmentId,
          name: file.metadata.filename,
          size: file.metadata.size,
          type: file.metadata.mime_type,
          file_url: fileUrl,
          url: resolveUrl(fileUrl),
          preview: isImage({ type: file.metadata.mime_type })
            ? resolveUrl(fileUrl)
            : null,
        };

        this.existingAttachments[requisiteTypeId].push(
          fullAttachment
        );
        this.attachmentIds[requisiteTypeId].push(attachmentId);
      });
    },

    // Работа с вложениями
    setAttachmentIds(requisiteTypeId, attachmentIds) {
      this.attachmentIds[requisiteTypeId] = attachmentIds;
      if (this.existingAttachments[requisiteTypeId]) {
        this.existingAttachments[requisiteTypeId] =
          this.existingAttachments[requisiteTypeId].filter(
            (attachment) => attachmentIds.includes(attachment.id)
          );
      }
    },

    // Сохранение пользовательского поля
    async saveRequisite(
      requisiteType,
      editFormData,
      requisitable_id,
      requisitable_type
    ) {
      const payload = {
        requisite_type_id: requisiteType.id,
        requisitable_type,
        requisitable_id,
        data: editFormData,
        attachment_ids: this.attachmentIds[requisiteType.id],
      };

      const existingReq = this.requisites.find(
        (r) => r.requisite_type.id === requisiteType.id
      );

      try {
        if (existingReq) {
          await requisitesApi.update(
            this.companyId,
            existingReq.id,
            payload
          );
        } else {
          payload.attachment_ids ??= [];
          await requisitesApi.create(this.companyId, payload);
        }
      } catch (error) {
        console.error("Ошибка при сохранении пользовательского поля:", error);
        throw error;
      }
    },

    // Загрузка пользовательских полей для создания
    async loadRequisiteTypes(allowedModels) {
      try {
        const response = await requisiteTypesApi.index(
          this.companyId,
          {
            allowed_models: allowedModels,
            enabled: true,
          }
        );
        this.requisiteTypes = response.data.result || [];
        return this.requisiteTypes;
      } catch (error) {
        console.error("Ошибка при загрузке пользовательских полей:", error);
        throw error;
      }
    },

    // Формирование payload для создания
    getCreatePayload(requisitableType, formData, attachments, id) {
      return this.requisiteTypes
        .filter((type) => {
          const data = formData[type.code] || {};
          const hasValues = Object.values(data).some(
            (v) => v !== "" && v !== undefined && v !== null
          );
          const hasAttachments = attachments[type.code]?.length > 0;
          return hasValues || hasAttachments;
        })
        .map((type) => ({
          requisite_type_id: type.id,
          requisitable_type: requisitableType,
          requisitable_id: id,
          data: { ...(formData[type.code] || {}) },
          attachment_ids: [...(attachments[type.code] || [])],
        }));
    },

    // Сохранение пользовательских полей для создания
    async createRequisites(requisites) {
      await Promise.all(
        requisites.map((requisite) =>
          requisitesApi.create(this.companyId, requisite)
        )
      );
    },

    reset() {
      this.requisites = [];
      this.requisiteTypes = [];
      this.attachmentIds = {};
      this.existingAttachments = {};
      this.loading = false;
    },
  },
});
